import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, createStyles, Theme, Typography } from '@bb-ui/react-library';
import { orderBy, get, debounce, uniqBy } from 'lodash';
import { PageTemplate } from 'components/PageTemplate';
import { ErrorMessage } from 'components/ErrorMessage';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { LearnSitesTable, LockSort } from './LearnSitesTable';
import { TextField, MenuItem } from '@bb-ui/react-library/dist/components';
import { PrimaryButton } from '@bb-ui/react-library/dist/components/Button';
import { Download } from '@bb-ui/icons/dist/small';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { useReaperLogDownload } from './hooks/useReaperLogDownload';
import { SITE_TYPE_FILTERS, SiteTypeFilter } from 'utils/siteType';
import { useSharedDatabases } from './hooks/useSharedDatabases';
import { useLearnSitePages } from './hooks/useLearnSitePages';
import { useLockStates } from './hooks/useSiteLocked';
import { LearnSitesFilterMenu } from './LearnSitesFilterMenu';
import {
  DATABASE_FILTERS,
  DEFAULT_ENABLED_FILTERS,
  DEFAULT_FILTER_VALUES,
  DatabaseFilter,
  LOCK_FILTERS,
  LockFilter,
  LearnSiteFilterKey,
  LearnSiteFilterValues,
  countUncheckedLocks,
  getSiteDatabase,
  matchesLearnSiteFilters,
  needsLockStates,
} from './learnSiteFilters';

const POWER_BI_REPORT_URL =
  'https://app.powerbi.com/links/HjBJT5C_C6?ctid=75853e87-aaca-4625-a00a-5a7ca2fe1a72&pbi_source=linkShare&bookmarkGuid=57f89617-afde-4579-8c7e-0af24fc5d9b4';
const POWER_BI_REPORT_URL_GOV =
  'https://app.powerbi.com/links/lo_GjNAFsk?ctid=75853e87-aaca-4625-a00a-5a7ca2fe1a72&pbi_source=linkShare';

const ALL_REGIONS = 'all';

const CLEAN_UP_LOG_STAGES = ['local', 'dev', 'int', 'tp'];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    filtersContainer: {
      display: 'flex',
      marginBottom: theme.spacing(4),
      gap: theme.spacing(2),
      alignItems: 'flex-start',
      flexWrap: 'wrap',
    },
    regionSelect: {
      backgroundColor: theme.palette.background.paper,
      minWidth: 150,
    },
    searchField: {
      flexGrow: 1,
    },
    siteCount: {
      marginBottom: theme.spacing(1),
    },
    lockNote: {
      marginBottom: theme.spacing(1),
      color: theme.palette.text.secondary,
    },
    partialError: {
      marginBottom: theme.spacing(2),
    },
    helperText: {
      color: theme.palette.text.secondary,
      transition: 'opacity 0.2s ease',
    },
  }),
);

export const LearnSites: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const env = useAppConfigContext();
  const regions = env.houstonSupportedRegions ?? ['us-east-1'];
  const isProd = env.stage === 'prod';
  const showCleanUpLog = CLEAN_UP_LOG_STAGES.includes(env.stage ?? '');
  const isGov = regions.includes('us-gov-west-1');
  const reportUrl = isGov ? POWER_BI_REPORT_URL_GOV : POWER_BI_REPORT_URL;

  const { downloadCleanUpLog, isDownloading: isDownloadingCleanUpLog } = useReaperLogDownload();

  const [selectedRegion, setSelectedRegion] = React.useState<string>(regions[0]);
  const isAllRegions = selectedRegion === ALL_REGIONS;
  const regionsKey = regions.join(',');
  const loadedRegions = React.useMemo(
    () => (isAllRegions ? regionsKey.split(',') : [selectedRegion]),
    [isAllRegions, selectedRegion, regionsKey],
  );
  const [enabledFilters, setEnabledFilters] = React.useState<Set<LearnSiteFilterKey>>(
    () => new Set(DEFAULT_ENABLED_FILTERS),
  );
  const [filterValues, setFilterValues] =
    React.useState<LearnSiteFilterValues>(DEFAULT_FILTER_VALUES);
  const [inputValue, setInputValue] = React.useState<string>('');
  const [searchFilter, setSearchFilter] = React.useState<string>('');
  const [isSearching, setIsSearching] = React.useState<boolean>(false);
  const [searchExhausted, setSearchExhausted] = React.useState<boolean>(false);
  const [isSearchFocused, setIsSearchFocused] = React.useState<boolean>(false);
  const [lockSort, setLockSort] = React.useState<LockSort>('none');

  const mountedRef = React.useRef(true);
  // Dedupe search pagination: avoid re-fetching the same nextPageToken when the
  // auto-load effect re-renders during the walk.
  const lastRequestedTokenRef = React.useRef<string | undefined>(undefined);
  // Larger page size while walking pages during search (initial load uses the
  // API default of 100). Caps at the backend MAX_PAGE_SIZE.
  const SEARCH_PAGE_SIZE = 1000;

  // One region or all of them page through the same loader, each region with its own page token
  const {
    sites,
    loading,
    error,
    failedRegions,
    nextPageToken,
    allDataFetched,
    loadMore: loadMorePages,
  } = useLearnSitePages(loadedRegions);

  const debouncedSetSearch = React.useMemo(
    () =>
      debounce((value: string) => {
        if (mountedRef.current) {
          setSearchFilter(value);
          setIsSearching(value.trim() !== '');
          setSearchExhausted(false);
        }
      }, 250),
    [],
  );

  const resetState = React.useCallback(() => {
    if (mountedRef.current) {
      setInputValue('');
      setSearchFilter('');
      setIsSearching(false);
      setSearchExhausted(false);
      lastRequestedTokenRef.current = undefined;
    }
    debouncedSetSearch.cancel();
  }, [debouncedSetSearch]);

  React.useEffect(
    () => () => {
      mountedRef.current = false;
      debouncedSetSearch.cancel();
    },
    [debouncedSetSearch],
  );

  // A new region starts with an empty search; useLearnSitePages starts its pages over
  React.useEffect(() => {
    resetState();
  }, [selectedRegion, resetState]);

  // The Database and Lock columns cost extra requests (a shared RDS lookup per fleet, a lock
  // lookup per site), so they are only shown, and their data only read, once their filter is on
  const showDatabase = enabledFilters.has('database');
  const showLock = enabledFilters.has('locked');
  const databases = useSharedDatabases(loadedRegions, showDatabase);

  // Lock state is read site by site, so it is only read for the whole loaded list while the
  // Locked filter narrows the list or the list is sorted by lock
  const scopedTenantIds = React.useMemo(
    () =>
      sites
        .filter(
          (site) =>
            site.FleetId?.toLowerCase() !== 'legacy' &&
            (isAllRegions || site.Region === selectedRegion),
        )
        .map((site) => site.TenantId)
        .filter(Boolean),
    [sites, isAllRegions, selectedRegion],
  );
  const readAllLocks =
    showLock && (needsLockStates(enabledFilters, filterValues) || lockSort !== 'none');
  const { locks } = useLockStates(scopedTenantIds, readAllLocks);
  const uncheckedLocks = React.useMemo(
    () => (readAllLocks ? countUncheckedLocks(scopedTenantIds, locks) : { pending: 0, failed: 0 }),
    [readAllLocks, scopedTenantIds, locks],
  );

  const nonLegacySites = React.useMemo(
    () => sites.filter((site) => site.FleetId?.toLowerCase() !== 'legacy'),
    [sites],
  );

  const statusOptions = React.useMemo(
    () => Array.from(new Set(nonLegacySites.map((s) => s.Status))).sort(),
    [nonLegacySites],
  );

  const fleetOptions = React.useMemo(
    () => Array.from(new Set(nonLegacySites.map((s) => s.FleetId).filter(Boolean))).sort(),
    [nonLegacySites],
  );

  const handleRegionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRegion(e.target.value);
    // Fleets belong to one region
    setFilterValues((prev) => ({ ...prev, fleet: DEFAULT_FILTER_VALUES.fleet }));
    if (searchFilter.trim()) {
      setIsSearching(false);
      setSearchExhausted(false);
      setTimeout(() => {
        if (mountedRef.current) {
          setIsSearching(searchFilter.trim() !== '');
          setSearchExhausted(false);
        }
      }, 100);
    }
  };

  const handleToggleFilter = (key: LearnSiteFilterKey) => {
    setEnabledFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    // A filter switched back on starts from no narrowing, except Status which starts at Installed.
    setFilterValues((prev) => ({ ...prev, [key]: DEFAULT_FILTER_VALUES[key] }));
    // The lock sort lives on the lock column, which goes away with the Locked filter
    if (key === 'locked') {
      setLockSort('none');
    }
  };

  const handleFilterValueChange =
    <K extends LearnSiteFilterKey>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value as LearnSiteFilterValues[K];
      setFilterValues((prev) => ({ ...prev, [key]: value }));
    };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setInputValue(value);
    debouncedSetSearch(value);
  };

  const filteredSites = React.useMemo(() => {
    const query = searchFilter.trim().toLowerCase();

    let filtered = sites.filter((site) => {
      // Exclude legacy sites
      if (site.FleetId?.toLowerCase() === 'legacy') {
        return false;
      }

      if (!isAllRegions && site.Region !== selectedRegion) {
        return false;
      }

      if (!matchesLearnSiteFilters(site, enabledFilters, filterValues, databases, locks)) {
        return false;
      }

      if (query) {
        const privateUrl =
          site.InstallationId && site.FleetId
            ? `${site.InstallationId}.${site.FleetId}.blackboard.com`
            : '';

        const searchableFields = [
          site.InstallationId || '',
          site.Name || '',
          site.Url || '',
          site.ClientName || '',
          privateUrl,
          site.TenantId || '',
          site.SalesforceId || '',
          // Shared RDS names contain "shared", so searching for it lists shared RDS sites, as in
          // Captain. The names are only fully known while the Database filter has loaded them, so
          // they are only searched then; otherwise the same search would depend on a hidden filter
          showDatabase ? getSiteDatabase(site, databases).name || '' : '',
        ].map((field) => field.toLowerCase());

        return searchableFields.some((field) => field.includes(query));
      }

      return true;
    });

    filtered = uniqBy(filtered, 'TenantId');
    const byInstallationId = (site: (typeof filtered)[number]) =>
      get(site, 'InstallationId')?.toLowerCase() ?? '';
    if (lockSort === 'none') {
      return orderBy(filtered, [byInstallationId], ['asc']);
    }
    // Sites whose lock state is unknown or not read yet go last in either direction, rather than
    // being mixed in with the unlocked sites
    const unknownLast = (site: (typeof filtered)[number]) =>
      typeof locks[site.TenantId] === 'boolean' ? 0 : 1;
    const lockRank = (site: (typeof filtered)[number]) => (locks[site.TenantId] ? 1 : 0);
    return orderBy(
      filtered,
      [unknownLast, lockRank, byInstallationId],
      ['asc', lockSort === 'lockedFirst' ? 'desc' : 'asc', 'asc'],
    );
  }, [
    sites,
    enabledFilters,
    filterValues,
    databases,
    showDatabase,
    locks,
    lockSort,
    searchFilter,
    selectedRegion,
    isAllRegions,
  ]);

  // Locked first, then locked last, then back to unsorted, which stops reading every lock state
  const handleToggleLockSort = () =>
    setLockSort((prev) => {
      if (prev === 'none') return 'lockedFirst';
      if (prev === 'lockedFirst') return 'lockedLast';
      return 'none';
    });

  // Auto-load more sites if searching
  React.useEffect(() => {
    if (isSearching && !loading && mountedRef.current && nextPageToken && !allDataFetched) {
      const hasNoResults = filteredSites.length === 0;
      const hasFewResults = filteredSites.length > 0 && filteredSites.length < 30;
      const shouldContinueSearch = hasNoResults || hasFewResults;

      if (
        shouldContinueSearch &&
        !searchExhausted &&
        lastRequestedTokenRef.current !== nextPageToken
      ) {
        lastRequestedTokenRef.current = nextPageToken;
        loadMorePages(SEARCH_PAGE_SIZE);
      }
    } else if (
      isSearching &&
      (!nextPageToken || allDataFetched) &&
      filteredSites.length === 0 &&
      mountedRef.current
    ) {
      setSearchExhausted(true);
    }
  }, [
    isSearching,
    loading,
    filteredSites.length,
    nextPageToken,
    searchExhausted,
    allDataFetched,
    loadMorePages,
  ]);

  const loadMore = () => {
    if (nextPageToken && !allDataFetched && mountedRef.current) {
      loadMorePages(SEARCH_PAGE_SIZE);
    }
  };

  const initialLoad = loading && sites.length === 0;
  const tableLoad = sites.length > 0;

  let content;
  // A failure with no sites at all replaces the list; when only some regions failed, the sites of
  // the others still show, under a warning
  if (error && sites.length === 0 && !loading) {
    content = (
      <ErrorMessage
        data-testid="learn-site-list-error"
        title={t('learnSites.loadError')}
        message={error.message}
        variant="block"
      />
    );
  } else if (initialLoad) {
    content = <LoadingIndicator data-testid="site-list-init" />;
  } else {
    content = (
      <LearnSitesTable
        sites={filteredSites}
        nextPageToken={nextPageToken}
        loading={loading && tableLoad}
        loadMore={loadMore}
        isSearching={isSearching}
        databases={databases}
        showDatabase={showDatabase}
        showLock={showLock}
        lockSort={lockSort}
        onToggleLockSort={handleToggleLockSort}
        searchExhausted={searchExhausted}
        allDataFetched={allDataFetched}
      />
    );
  }

  return (
    <PageTemplate title={t('learnSites.pageTitle')} data-testid="license-list-page">
      <div className={classes.filtersContainer}>
        <TextField
          select
          id="region-select"
          label={t('learnSites.selectRegionLabel')}
          value={selectedRegion}
          onChange={handleRegionChange}
          className={classes.regionSelect}
          floatingLabel
        >
          {regions.length > 1 && <MenuItem value={ALL_REGIONS}>{t('learnSites.all')}</MenuItem>}
          {regions.map((region) => (
            <MenuItem key={region} value={region}>
              {region}
            </MenuItem>
          ))}
        </TextField>

        <LearnSitesFilterMenu enabledFilters={enabledFilters} onToggleFilter={handleToggleFilter} />

        {enabledFilters.has('createdFrom') && (
          <TextField
            id="created-from-filter"
            type="date"
            label={t('learnSites.filters.createdFrom')}
            value={filterValues.createdFrom}
            onChange={handleFilterValueChange('createdFrom')}
            className={classes.regionSelect}
            floatingLabel
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: filterValues.createdTo || undefined }}
          />
        )}

        {enabledFilters.has('createdTo') && (
          <TextField
            id="created-to-filter"
            type="date"
            label={t('learnSites.filters.createdTo')}
            value={filterValues.createdTo}
            onChange={handleFilterValueChange('createdTo')}
            className={classes.regionSelect}
            floatingLabel
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: filterValues.createdFrom || undefined }}
          />
        )}

        {enabledFilters.has('status') && (
          <TextField
            select
            id="status-select"
            label={t('learnSites.selectStatusLabel')}
            value={filterValues.status}
            onChange={handleFilterValueChange('status')}
            className={classes.regionSelect}
            floatingLabel
          >
            <MenuItem value="All">{t('learnSites.all')}</MenuItem>
            {statusOptions.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        )}

        {enabledFilters.has('fleet') && (
          <TextField
            select
            id="fleet-select"
            label={t('learnSites.filters.fleet')}
            value={filterValues.fleet}
            onChange={handleFilterValueChange('fleet')}
            className={classes.regionSelect}
            floatingLabel
          >
            <MenuItem value="All">{t('learnSites.all')}</MenuItem>
            {fleetOptions.map((fleet) => (
              <MenuItem key={fleet} value={fleet}>
                {fleet}
              </MenuItem>
            ))}
          </TextField>
        )}

        {enabledFilters.has('siteType') && (
          <TextField
            select
            id="site-type-select"
            label={t('learnSites.selectSiteTypeLabel')}
            value={filterValues.siteType}
            onChange={handleFilterValueChange('siteType')}
            className={classes.regionSelect}
            floatingLabel
          >
            {SITE_TYPE_FILTERS.map((option: SiteTypeFilter) => (
              <MenuItem key={option} value={option}>
                {t(`learnSites.siteTypes.${option}`)}
              </MenuItem>
            ))}
          </TextField>
        )}

        {enabledFilters.has('locked') && (
          <TextField
            select
            id="locked-select"
            label={t('learnSites.filters.locked')}
            value={filterValues.locked}
            onChange={handleFilterValueChange('locked')}
            className={classes.regionSelect}
            floatingLabel
          >
            {LOCK_FILTERS.map((option: LockFilter) => (
              <MenuItem key={option} value={option}>
                {t(`learnSites.lockTypes.${option}`)}
              </MenuItem>
            ))}
          </TextField>
        )}

        {enabledFilters.has('database') && (
          <TextField
            select
            id="database-select"
            label={t('learnSites.filters.database')}
            value={filterValues.database}
            onChange={handleFilterValueChange('database')}
            className={classes.regionSelect}
            floatingLabel
          >
            {DATABASE_FILTERS.map((option: DatabaseFilter) => (
              <MenuItem key={option} value={option}>
                {t(`learnSites.databaseTypes.${option}`)}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          id="search-field"
          label={t('learnSites.searchLabel')}
          value={inputValue}
          onChange={handleSearchChange}
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
          className={classes.searchField}
          floatingLabel
          helperText={
            isSearchFocused && !inputValue.trim()
              ? t(
                  showDatabase
                    ? 'learnSites.searchHelperTextWithDatabase'
                    : 'learnSites.searchHelperText',
                )
              : ' '
          }
          FormHelperTextProps={{
            'aria-live': 'polite',
            className: classes.helperText,
          }}
        />

        {isProd && (
          <PrimaryButton
            data-testid="instance-report-button"
            onClick={() => window.open(reportUrl, '_blank', 'noopener,noreferrer')}
          >
            Instance Report
          </PrimaryButton>
        )}

        {showCleanUpLog && (
          <PrimaryButton
            data-testid="clean-up-log-button"
            variant="outlined"
            startIcon={<Download />}
            disabled={isDownloadingCleanUpLog}
            onClick={downloadCleanUpLog}
          >
            {isDownloadingCleanUpLog
              ? t('learnSites.cleanUpLogDownloading')
              : t('learnSites.cleanUpLogButton')}
          </PrimaryButton>
        )}
      </div>
      {error && sites.length > 0 && (
        <div className={classes.partialError}>
          <ErrorMessage
            data-testid="learn-site-list-partial-error"
            title={t('learnSites.regionsLoadError', { regions: failedRegions.join(', ') })}
            message={error.message}
            variant="inline"
          />
        </div>
      )}
      {!(error && sites.length === 0) && !initialLoad && (
        <Typography
          variant="body2"
          className={classes.siteCount}
          aria-live="polite"
          data-testid="site-count"
        >
          {allDataFetched
            ? t('learnSites.siteCount', { count: filteredSites.length })
            : t('learnSites.siteCountPartial', { count: filteredSites.length })}
        </Typography>
      )}
      {uncheckedLocks.pending > 0 && (
        <Typography variant="body2" className={classes.lockNote} data-testid="lock-pending">
          {t('learnSites.lockPending', { count: uncheckedLocks.pending })}
        </Typography>
      )}
      {uncheckedLocks.failed > 0 && (
        <Typography variant="body2" className={classes.lockNote} data-testid="lock-failed">
          {t('learnSites.lockFailed', { count: uncheckedLocks.failed })}
        </Typography>
      )}
      {content}
    </PageTemplate>
  );
};
